import React, { useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import InferenceStepBox from '../Steps/InferenceStepBox';
import CodeStepBox from '../Steps/CodeStepBox';
import ApiStepBox from '../Steps/ApiStepBox';
import ProceduralMemory from '../../../models/Memories/ProceduralMemory';
import InferenceChainStep from '../../../models/Steps/InferenceChainStep';
import CodeStep from '../../../models/Steps/CodeStep';
import ApiStep from '../../../models/Steps/ApiStep';
import { 
  TextField, 
  Button, 
  Select,
  MenuItem, 
  Switch, 
  Card, 
  Typography, 
  Chip,
  FormControl,
  InputLabel,
  Box,
  Stack,
  Paper,
  CardContent,
  IconButton
} from '@mui/material';
import { 
  Add as AddIcon, 
  Settings as SettingsIcon,
  Psychology as PsychologyIcon,
  DragIndicator as DragIndicatorIcon
} from '@mui/icons-material';
import { AVAILABLE_MODELS, DEFAULT_MODEL, DEFAULT_TEMPERATURE, ModelType } from '../../../config/models';
import ProceduralMemoryWizardModal from '../../Modals/ProceduralMemoryWizardModal';
import CognitiveAbilitiesModal from '../../Modals/CognitiveAbilitiesModal';

const getCostColor = (cost: string) => {
  switch (cost) {
    case 'Very High': return 'error';
    case 'High': return 'warning';
    case 'Medium': return 'primary';
    case 'Low': return 'success';
    case 'Very Low': return 'info';
    default: return 'default';
  }
};

interface ProceduralMemoryBoxProps {
  memory: ProceduralMemory;
  onMemoryChange: (memory: ProceduralMemory) => void;
  workflowInputs?: Record<string, any>;
  setWorkflowInputs?: (inputs: Record<string, any>) => void;
  nodeType?: string;
}

interface CognitiveAbility {
  enabled: boolean;
  steps: any[];
}

interface CognitiveAbilities {
  learn: CognitiveAbility;
  retrieve: CognitiveAbility;
  reason: CognitiveAbility;
  design: CognitiveAbility;
  reflect: CognitiveAbility;
}

const ProceduralMemoryBox: React.FC<ProceduralMemoryBoxProps> = React.memo(({ 
  memory,
  onMemoryChange,
  workflowInputs = {},
  setWorkflowInputs = () => {},
  nodeType = 'agent'
}) => {
  const [showWizardModal, setShowWizardModal] = React.useState(false);
  const [showCognitiveModal, setShowCognitiveModal] = React.useState(false);

  // Check node type restrictions
  const isWorkflowNode = nodeType === 'workflow';
  const isInputNode = nodeType === 'input_node';
  const isLoopNode = nodeType === 'loop';
  
  // Workflow nodes don't have procedural memory
  const hasProceduralMemory = !isWorkflowNode;
  
  // Input and loop nodes don't have cognitive abilities
  const hasCognitiveAbilities = !isInputNode && !isLoopNode && !isWorkflowNode;

  const handleModelChange = useCallback((event: any) => {
    const newModel = event.target.value as any;
    onMemoryChange(new ProceduralMemory(
      newModel,
      memory.modelTemperature,
      memory.steps,
      memory.cognitiveAbilities,
      memory.feedback
    ));
  }, [memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const handleTemperatureChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    if (value === '' || (parseFloat(value) >= 0 && parseFloat(value) <= 1)) {
      const newTemperature = parseFloat(value) || 0;
      onMemoryChange(new ProceduralMemory(
        memory.model,
        newTemperature,
        memory.steps,
        memory.cognitiveAbilities,
        memory.feedback
      ));
    }
  }, [memory.model, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const handleTemperatureStepChange = useCallback((step: number) => {
    const newValue = parseFloat(memory.modelTemperature.toString()) + step;
    if (newValue >= 0 && newValue <= 1) {
      const roundedValue = Number(newValue.toFixed(2));
      onMemoryChange(new ProceduralMemory(
        memory.model,
        roundedValue,
        memory.steps,
        memory.cognitiveAbilities,
        memory.feedback
      ));
    }
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const handleFeedbackChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const newFeedback = event.target.value;
    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      memory.steps,
      memory.cognitiveAbilities,
      newFeedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, onMemoryChange]);

  const handleCognitiveAbilityToggle = useCallback((ability: keyof CognitiveAbilities, enabled: boolean) => {
    const updatedAbilities = {
      ...memory.cognitiveAbilities,
      [ability]: {
        ...memory.cognitiveAbilities[ability],
        enabled
      }
    };
    
    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      memory.steps,
      updatedAbilities,
      memory.feedback
    ));
  }, [memory.cognitiveAbilities, memory.model, memory.modelTemperature, memory.steps, memory.feedback, onMemoryChange]);

  const handleWizardUpdate = useCallback((updatedMemory: ProceduralMemory) => {
    onMemoryChange(updatedMemory);
  }, [onMemoryChange]);

  const handleCognitiveAbilitiesUpdate = useCallback((updatedAbilities: CognitiveAbilities) => {
    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      memory.steps,
      updatedAbilities,
      memory.feedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.feedback, onMemoryChange]);

  const addStep = useCallback((type: 'inference_chain' | 'code' | 'api') => {
    let newStep: InferenceChainStep | CodeStep | ApiStep;

    switch (type) {
      case 'inference_chain':
        newStep = new InferenceChainStep(
          'New Inference Step',
          {
            model: memory.model as ModelType,
            model_temp: memory.modelTemperature,
            prompt: '',
            variables: null,
            retriever: null,
            is_streaming: false,
            condition: 'True'
          }
        );
        break;
      case 'code':
        newStep = new CodeStep(
          'New Code Step',
          {
            code: '',
            variables: null,
            condition: 'True'
          }
        );
        break;
      case 'api':
        newStep = new ApiStep(
          'New API Step',
          {
            type: 'GET',
            endpoint: '',
            headers: {},
            params: {},
            body: {},
            auth: null,
            timeout: null,
            condition: 'True'
          }
        );
        break;
      default:
        return;
    }

    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      [...memory.steps, newStep],
      memory.cognitiveAbilities,
      memory.feedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const removeStep = useCallback((id: string) => {
    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      memory.steps.filter(step => step.id !== id),
      memory.cognitiveAbilities,
      memory.feedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const handleStepUpdate = useCallback((updatedStep: InferenceChainStep | CodeStep | ApiStep) => {
    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      memory.steps.map(step => step.id === updatedStep.id ? updatedStep : step),
      memory.cognitiveAbilities,
      memory.feedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const onDragEnd = useCallback((result: any) => {
    if (!result.destination) return;

    const items = Array.from(memory.steps);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    onMemoryChange(new ProceduralMemory(
      memory.model,
      memory.modelTemperature,
      items,
      memory.cognitiveAbilities,
      memory.feedback
    ));
  }, [memory.model, memory.modelTemperature, memory.steps, memory.cognitiveAbilities, memory.feedback, onMemoryChange]);

  const cognitiveAbilityLabels = {
    learn: { label: 'Learn', icon: '📚', description: 'Learning and knowledge acquisition' },
    retrieve: { label: 'Retrieve', icon: '🔍', description: 'Information retrieval and search' },
    reason: { label: 'Reason', icon: '🧠', description: 'Logical reasoning and inference' },
    design: { label: 'Design', icon: '🎨', description: 'Creative design and planning' },
    reflect: { label: 'Reflect', icon: '🪞', description: 'Self-reflection and evaluation' }
  };

  // If this is a workflow node, show a disabled state message
  if (isWorkflowNode) {
    return (
      <Box sx={{ p: 3, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Procedural Memory Configuration</Typography>
          </Box>

          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Procedural Memory (Disabled for Workflow Nodes)
            </Typography>
            <Paper sx={{ p: 3, bgcolor: 'grey.50', textAlign: 'center' }}>
              <Typography variant="h3" sx={{ mb: 2 }}>🧠</Typography>
              <Typography variant="body1">
                Procedural memory is managed by the sub-workflow
              </Typography>
            </Paper>
          </Box>

          <Box>
            <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
              Cognitive Abilities (Disabled for Workflow Nodes)
            </Typography>
            <Paper sx={{ p: 3, bgcolor: 'grey.50', textAlign: 'center' }}>
              <Typography variant="h3" sx={{ mb: 2 }}>🧠</Typography>
              <Typography variant="body1">
                Cognitive abilities are managed by the sub-workflow
              </Typography>
            </Paper>
          </Box>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1, border: 1, borderColor: 'grey.300' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Header with Wizard Button */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="h6">Procedural Memory Configuration</Typography>
            {hasProceduralMemory && (
              <Button
                variant="contained"
                startIcon={<SettingsIcon />}
                onClick={() => setShowWizardModal(true)}
                size="small"
              >
                Advanced Setup
              </Button>
            )}
          </Box>

          {/* Model Configuration */}
          {hasProceduralMemory && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>Model Configuration</Typography>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth>
                      <InputLabel>Model</InputLabel>
                    <Select
                        value={memory.model}
                      onChange={handleModelChange}
                      label="Model"
                    >
                        {AVAILABLE_MODELS.map((modelOption) => (
                          <MenuItem key={modelOption.name} value={modelOption.name}>
                            <Stack direction="row" spacing={1} alignItems="center">
                              <Typography variant="body2">{modelOption.name}</Typography>
                              <Chip 
                                label={modelOption.cost} 
                                size="small" 
                                color={getCostColor(modelOption.cost)}
                              />
                            </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  </Box>

                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight="medium" gutterBottom>
                      Temperature
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleTemperatureStepChange(-0.01)}
                      >
                        -
                      </Button>
                      <TextField
                        value={memory.modelTemperature}
                        onChange={handleTemperatureChange}
                        size="small"
                        sx={{ width: 100 }}
                        inputProps={{ style: { textAlign: 'center' } }}
                      />
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => handleTemperatureStepChange(0.01)}
                      >
                        +
                      </Button>
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      Creativity level (0.0 - 1.0)
                    </Typography>
                  </Box>
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Cognitive Abilities */}
          {hasCognitiveAbilities && (
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Cognitive Abilities</Typography>
                  <Button
                    variant="outlined"
                    startIcon={<PsychologyIcon />}
                    onClick={() => setShowCognitiveModal(true)}
                    size="small"
                  >
                    Configure
                  </Button>
                </Box>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' }, gap: 2 }}>
                  {Object.entries(cognitiveAbilityLabels).map(([key, { label, icon, description }]) => (
                    <Paper key={key} sx={{ p: 2, border: 1, borderColor: 'divider' }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="h6">{icon}</Typography>
                          <Typography variant="subtitle2">{label}</Typography>
                        </Box>
                        <Switch
                          checked={memory.cognitiveAbilities[key as keyof CognitiveAbilities].enabled}
                          onChange={(e) => handleCognitiveAbilityToggle(key as keyof CognitiveAbilities, e.target.checked)}
                          size="small"
                    />
                  </Box>
                      <Typography variant="caption" color="text.secondary">
                        {description}
                      </Typography>
                    </Paper>
                  ))}
                </Box>
              </CardContent>
            </Card>
              )}

          {/* Steps Configuration */}
          {hasProceduralMemory && (
            <Card>
              <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="h6">Steps Configuration</Typography>
                  <Stack direction="row" spacing={1}>
                      <Button 
                      variant="outlined"
                        size="small" 
                      startIcon={<AddIcon />}
                        onClick={() => addStep('inference_chain')}
                      >
                      Add Inference
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                      startIcon={<AddIcon />}
                        onClick={() => addStep('code')}
                      >
                      Add Code
                      </Button>
                      <Button 
                        variant="outlined" 
                        size="small" 
                      startIcon={<AddIcon />}
                        onClick={() => addStep('api')}
                      >
                      Add API
                      </Button>
                  </Stack>
                  </Box>

                {memory.steps.length === 0 ? (
                  <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'grey.50' }}>
                    <Typography variant="body1" color="text.secondary">
                      No steps configured. Add steps to define the procedural memory.
                    </Typography>
                  </Paper>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="steps">
                      {(provided) => (
                        <Box {...provided.droppableProps} ref={provided.innerRef}>
                          <Stack spacing={2}>
                            {memory.steps.map((step, index) => (
                              <Draggable key={step.id} draggableId={step.id} index={index}>
                                {(provided, snapshot) => (
                                  <Box
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    sx={{
                                      transform: snapshot.isDragging ? provided.draggableProps.style?.transform : 'none',
                                    }}
                                  >
                                    <Card sx={{ 
                                      border: 1, 
                                      borderColor: 'divider',
                                      '&:hover': { borderColor: 'primary.main' }
                                    }}>
                                      <CardContent>
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <IconButton
                                              {...provided.dragHandleProps}
                                              size="small"
                                              sx={{ cursor: 'grab' }}
                                            >
                                              <DragIndicatorIcon />
                                            </IconButton>
                                            <Typography variant="subtitle1">
                                              {step.description} ({step.type})
                                            </Typography>
                                          </Box>
                                          
                                          <Button
                                            color="error"
                                            size="small"
                                            onClick={() => removeStep(step.id)}
                                            sx={{ 
                                              opacity: 0,
                                              '.MuiCard-root:hover &': { opacity: 1 },
                                              transition: 'opacity 0.2s'
                                            }}
                                          >
                                            Remove
                                          </Button>
                                        </Box>
                                        
                                        {step.type === 'inference_chain' && (
                                          <InferenceStepBox
                                            stepData={step}
                                            onUpdate={handleStepUpdate}
                                            workflowInputs={workflowInputs}
                                            setWorkflowInputs={setWorkflowInputs}
                                          />
                                        )}
                                        {step.type === 'code' && (
                                          <CodeStepBox
                                            stepData={step}
                                            onUpdate={handleStepUpdate}
                                            workflowInputs={workflowInputs}
                                          />
                                        )}
                                        {step.type === 'api' && (
                                          <ApiStepBox
                                            stepData={step}
                                            onUpdate={handleStepUpdate}
                                            workflowInputs={workflowInputs}
                                            setWorkflowInputs={setWorkflowInputs}
                                          />
                                        )}
                                      </CardContent>
                                    </Card>
                                  </Box>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </Stack>
                        </Box>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </CardContent>
            </Card>
          )}

          {/* Feedback Configuration */}
          {hasProceduralMemory && (
            <Card>
                            <CardContent>
                <Typography variant="h6" gutterBottom>Feedback Configuration</Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  label="Feedback"
                  value={memory.feedback}
                  onChange={handleFeedbackChange}
                  placeholder="Enter feedback for the procedural memory..."
                />
                            </CardContent>
                          </Card>
          )}
        </Box>
      </Box>

      {hasProceduralMemory && (
        <>
          <ProceduralMemoryWizardModal
            open={showWizardModal}
            onClose={() => setShowWizardModal(false)}
            proceduralMemory={memory}
            onUpdate={handleWizardUpdate}
            workflowInputs={workflowInputs}
            setWorkflowInputs={setWorkflowInputs}
            nodeType={nodeType}
          />
          
          {hasCognitiveAbilities && (
            <CognitiveAbilitiesModal
              open={showCognitiveModal}
              onClose={() => setShowCognitiveModal(false)}
              cognitiveAbilities={memory.cognitiveAbilities}
              onUpdate={handleCognitiveAbilitiesUpdate}
              workflowInputs={workflowInputs}
              setWorkflowInputs={setWorkflowInputs}
            />
          )}
        </>
      )}
    </>
  );
});

ProceduralMemoryBox.displayName = 'ProceduralMemoryBox';

export default ProceduralMemoryBox;